// Firebase Topic Note Service
// ============================
// Simple CRUD for topic notes (Written Preparation feature)
// Replaces the 540-line notesService.ts aggregation pipeline

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { where } from './base';
import type { TopicNote } from '../../types';

const COLLECTION = 'topicNotes';

/**
 * Get or create a topic note. Returns existing note if found, creates empty one if not.
 */
export const getTopicNote = async (
  userId: string,
  topicId: string
): Promise<{ success: boolean; data?: TopicNote; error?: string }> => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('topicId', '==', topicId)
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const docSnap = snapshot.docs[0];
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } as TopicNote };
    }
    return { success: true, data: undefined };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Save (create or update) a topic note.
 * Uses the topicId as document ID for easy upsert behavior.
 */
export const saveTopicNote = async (
  note: Omit<TopicNote, 'id' | 'createdAt' | 'updatedAt'>
): Promise<{ success: boolean; id?: string; error?: string }> => {
  try {
    const docId = `${note.userId}_${note.topicId}`;
    const docRef = doc(db, COLLECTION, docId);
    const existing = await getDoc(docRef);

    const now = Timestamp.now();
    await setDoc(docRef, {
      ...note,
      updatedAt: now,
      ...(existing.exists() ? {} : { createdAt: now }),
    }, { merge: true });

    return { success: true, id: docId };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Get all topic notes for a user, optionally filtered by journey.
 */
export const getTopicNotes = async (
  userId: string,
  journeyId?: string
): Promise<{ success: boolean; data?: TopicNote[]; error?: string }> => {
  try {
    const constraints = [where('userId', '==', userId)];
    if (journeyId) {
      constraints.push(where('journeyId', '==', journeyId));
    }

    const q = query(collection(db, COLLECTION), ...constraints);
    const snapshot = await getDocs(q);
    const notes = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as TopicNote));

    return { success: true, data: notes };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Delete a topic note.
 */
export const deleteTopicNote = async (
  noteId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    await deleteDoc(doc(db, COLLECTION, noteId));
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
